import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Per-session cache-bust token. Generated once at module load — i.e., bumped
// every time the page hard-reloads (including the header Refresh button).
// Appended as `?_v=<token>` to all GET requests so the browser HTTP cache
// (Cache-Control: max-age=30 on safe list endpoints) gets a guaranteed miss
// after a manual refresh, while still de-duping repeat calls within a session.
const SESSION_VERSION = Date.now();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  // Send httpOnly session cookie along with every request (iter184 secure-cookie migration).
  // The backend accepts EITHER the Bearer header or the cookie so we can transition safely.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: append per-session cache-bust token to GETs. Auth
// travels via the httpOnly `optsc_session` cookie (see withCredentials:true
// above) — we no longer read auth_token from localStorage on the client.
api.interceptors.request.use(
  (config) => {
    if ((config.method || 'get').toLowerCase() === 'get') {
      config.params = { ...(config.params || {}), _v: SESSION_VERSION };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('user');
      localStorage.removeItem('locations');
      localStorage.removeItem('current_location');
      localStorage.removeItem('permissions');
      localStorage.removeItem('impersonated_by');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Locations API
export const locationsApi = {
  getAll: () => api.get('/locations'),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
  testGHL: (id, key) => api.post(`/locations/${id}/test-ghl`, null, { params: key ? { key } : {} }),
  testJuvonno: (id) => api.post(`/locations/${id}/test-juvonno`),
};

// Users API
export const usersApi = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  logoutAll: (id) => api.post(`/users/${id}/logout-all`),
  impersonate: (id) => api.post(`/users/${id}/impersonate`),
  stopImpersonation: () => api.post('/users/impersonate/stop'),
};

// Roles API
export const rolesApi = {
  getAll: () => api.get('/roles'),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

export default api;
